import {
    Brush,
    Link2,
    Magnet,
    Paintbrush,
    Paintbrush2,
    Unlink,
    Waypoints,
} from "lucide-react";
import { useSvgDiagram2D } from "./SvgDiagram2DContext";
import type {
    ConnectSelectionMode,
    InteractFalloff,
    MegaNodeBrushMode,
    NodeBrushConnectMode,
    NodeBrushLayout,
    NodeInteractAction,
    NodeInteractConnectMode,
} from "../../utils/svgDiagram2DModel";
import {
    INTERACT_FALLOFFS,
    INTERACT_FALLOFF_LABELS,
    NODE_INTERACT_CONNECT_MODES,
    NODE_INTERACT_CONNECT_MODE_LABELS,
} from "../../utils/svgDiagram2DModel";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { CheckboxField } from "../reusable/CheckboxField";

function num(v: string | number, fallback: number): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
}

const CONNECT_MODE_OPTIONS: ReadonlyArray<{
    value: ConnectSelectionMode;
    label: string;
}> = [
    { value: "complete", label: "Every node to every other" },
    { value: "sequence", label: "In sequence (selection order)" },
    { value: "nearest", label: "Each node to nearest neighbor" },
];

/**
 * Options for the Connect-selection subtool: the wiring pattern across the
 * selected nodes plus optional min/max proximity thresholds, and the apply /
 * clear actions. Mirrors the C / X shortcuts but with the pattern and distance
 * filters exposed. Select nodes on the canvas (click, shift-click, or marquee —
 * this subtool selects like the Select tool), then connect them.
 */
function ConnectSelectionOptionsPanel() {
    const {
        connectSelectionOptions: opts,
        setConnectSelectionOptions,
        connectSelectedNodes,
        clearConnectionsBetweenSelected,
        selectedNodeIds,
        activePresetId,
    } = useSvgDiagram2D();

    const enoughSelected = selectedNodeIds.length >= 2;

    return (
        <>
            <label className="field-label">
                Connection pattern
                <select
                    className="anchor-select"
                    value={opts.mode}
                    onChange={(e) =>
                        setConnectSelectionOptions({
                            mode: e.target.value as ConnectSelectionMode,
                        })
                    }
                >
                    {CONNECT_MODE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </label>

            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">
                    Proximity threshold
                </p>
                <p
                    className="path-tool-meta"
                    style={{ margin: "0 0 8px", opacity: 0.7 }}
                >
                    Only connect node pairs whose center distance falls within the
                    enabled bounds.
                </p>
                <CheckboxField
                    checked={opts.minDistanceEnabled}
                    onChange={(checked) =>
                        setConnectSelectionOptions({
                            minDistanceEnabled: checked,
                        })
                    }
                    label="Minimum distance"
                />
                {opts.minDistanceEnabled && (
                    <label className="field-label" style={{ marginTop: 6 }}>
                        Min distance
                        <DebouncedInput
                            type="number"
                            min={0}
                            step={1}
                            value={opts.minDistance}
                            onChange={(v) =>
                                setConnectSelectionOptions({
                                    minDistance: Math.max(
                                        0,
                                        num(v, opts.minDistance),
                                    ),
                                })
                            }
                        />
                    </label>
                )}
                <div style={{ marginTop: 8 }}>
                    <CheckboxField
                        checked={opts.maxDistanceEnabled}
                        onChange={(checked) =>
                            setConnectSelectionOptions({
                                maxDistanceEnabled: checked,
                            })
                        }
                        label="Maximum distance"
                    />
                </div>
                {opts.maxDistanceEnabled && (
                    <label className="field-label" style={{ marginTop: 6 }}>
                        Max distance
                        <DebouncedInput
                            type="number"
                            min={0}
                            step={1}
                            value={opts.maxDistance}
                            onChange={(v) =>
                                setConnectSelectionOptions({
                                    maxDistance: Math.max(
                                        0,
                                        num(v, opts.maxDistance),
                                    ),
                                })
                            }
                        />
                    </label>
                )}
            </div>

            <div className="diagram-node-panel-section">
                <span className="path-tool-meta" style={{ opacity: 0.7 }}>
                    {selectedNodeIds.length} node
                    {selectedNodeIds.length === 1 ? "" : "s"} selected
                </span>
                <div
                    className="field-row"
                    style={{ gap: 6, marginTop: 8, flexWrap: "wrap" }}
                >
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!enoughSelected || !activePresetId}
                        onClick={connectSelectedNodes}
                        title="Connect the selected nodes with the options above (C)"
                    >
                        <Link2 size={14} aria-hidden /> Connect
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!enoughSelected}
                        onClick={clearConnectionsBetweenSelected}
                        title="Remove connections between the selected nodes (X)"
                    >
                        <Unlink size={14} aria-hidden /> Clear
                    </button>
                </div>
                {!activePresetId && (
                    <p
                        className="path-tool-meta"
                        style={{ margin: "6px 0 0", opacity: 0.7 }}
                    >
                        Add a connection preset to enable connecting.
                    </p>
                )}
            </div>
        </>
    );
}

const INTERACT_ACTION_OPTIONS: ReadonlyArray<{
    value: NodeInteractAction;
    label: string;
}> = [
    { value: "attract", label: "Attract (pull in)" },
    { value: "repel", label: "Repel (push out)" },
    { value: "connect", label: "Connect (proximity)" },
];

/**
 * Options for the node-interact subtool. Unlike the brushes this subtool is
 * dispatched on a node — click a node on the canvas to apply one pulse. The
 * `attract`/`repel` actions move neighbors within the radius (by strength +
 * falloff); the `connect` action wires the clicked node to its neighbors instead
 * (directly, or bridged through the nearest nodes), so its strength/falloff
 * fields are swapped for the connection mode.
 */
function InteractOptionsPanel() {
    const {
        nodeInteractOptions: opts,
        setNodeInteractOptions,
        activePresetId,
    } = useSvgDiagram2D();

    const isConnect = opts.action === "connect";

    return (
        <>
            <label className="field-label">
                Action
                <select
                    className="anchor-select"
                    value={opts.action}
                    onChange={(e) =>
                        setNodeInteractOptions({
                            action: e.target.value as NodeInteractAction,
                        })
                    }
                >
                    {INTERACT_ACTION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </label>

            <label className="field-label" style={{ marginTop: 8 }}>
                Radius
                <DebouncedInput
                    type="number"
                    min={0}
                    step={1}
                    value={opts.radius}
                    onChange={(v) =>
                        setNodeInteractOptions({
                            radius: Math.max(0, num(v, opts.radius)),
                        })
                    }
                />
            </label>

            {isConnect ? (
                <>
                    <label className="field-label" style={{ marginTop: 8 }}>
                        Connection
                        <select
                            className="anchor-select"
                            value={opts.connectMode}
                            onChange={(e) =>
                                setNodeInteractOptions({
                                    connectMode: e.target
                                        .value as NodeInteractConnectMode,
                                })
                            }
                        >
                            {NODE_INTERACT_CONNECT_MODES.map((m) => (
                                <option key={m} value={m}>
                                    {NODE_INTERACT_CONNECT_MODE_LABELS[m]}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="field-label" style={{ marginTop: 8 }}>
                        Symmetry tolerance
                        <DebouncedInput
                            type="number"
                            min={0}
                            step={0.5}
                            value={opts.symmetryTolerance}
                            onChange={(v) =>
                                setNodeInteractOptions({
                                    symmetryTolerance: Math.max(
                                        0,
                                        num(v, opts.symmetryTolerance),
                                    ),
                                })
                            }
                        />
                    </label>
                    <p
                        className="path-tool-meta"
                        style={{ margin: "8px 0 0", opacity: 0.7 }}
                    >
                        Click a node to connect it to nearby nodes using the
                        active connection preset.{" "}
                        {opts.connectMode === "bridge"
                            ? "Bridge grows a tree out from the clicked node, so far nodes link through the nodes between them."
                            : "Direct links the clicked node straight to every node in range."}
                    </p>
                    <p
                        className="path-tool-meta"
                        style={{ margin: "6px 0 0", opacity: 0.7 }}
                    >
                        With symmetry on, the same pulse re-fires from the
                        clicked node's mirror counterpart. Symmetry tolerance is
                        how far that counterpart may sit from the exact mirror
                        position to still be matched — raise it for layouts that
                        are only roughly symmetric.
                    </p>
                    {!activePresetId && (
                        <p
                            className="path-tool-meta"
                            style={{ margin: "6px 0 0", opacity: 0.7 }}
                        >
                            Add a connection preset to enable connecting.
                        </p>
                    )}
                </>
            ) : (
                <>
                    <label className="field-label" style={{ marginTop: 8 }}>
                        Strength
                        <DebouncedInput
                            type="number"
                            min={0}
                            max={1}
                            step={0.05}
                            value={opts.strength}
                            onChange={(v) =>
                                setNodeInteractOptions({
                                    strength: Math.max(
                                        0,
                                        Math.min(1, num(v, opts.strength)),
                                    ),
                                })
                            }
                        />
                    </label>

                    <label className="field-label" style={{ marginTop: 8 }}>
                        Falloff
                        <select
                            className="anchor-select"
                            value={opts.falloff}
                            onChange={(e) =>
                                setNodeInteractOptions({
                                    falloff: e.target.value as InteractFalloff,
                                })
                            }
                        >
                            {INTERACT_FALLOFFS.map((f) => (
                                <option key={f} value={f}>
                                    {INTERACT_FALLOFF_LABELS[f]}
                                </option>
                            ))}
                        </select>
                    </label>

                    <p
                        className="path-tool-meta"
                        style={{ margin: "8px 0 0", opacity: 0.7 }}
                    >
                        Click a node to{" "}
                        {opts.action === "attract" ? "pull" : "push"} nearby
                        nodes{" "}
                        {opts.action === "attract" ? "toward" : "away from"} it.
                        Strength is the fraction moved per click at full
                        influence — click repeatedly to build up the effect.
                    </p>
                </>
            )}
        </>
    );
}

const LAYOUT_OPTIONS: ReadonlyArray<{ value: NodeBrushLayout; label: string }> =
    [
        { value: "sunflower", label: "Sunflower (even scatter)" },
        { value: "random", label: "Random scatter" },
        { value: "ring", label: "Ring" },
        { value: "grid", label: "Grid" },
        { value: "spiral", label: "Spiral" },
    ];

/**
 * Options for the node-brush composite subtool: spread radius, node count, and
 * whether/how the dropped nodes are connected and grouped.
 */
function NodeBrushOptionsPanel() {
    const { nodeBrushOptions, setNodeBrushOptions } = useSvgDiagram2D();
    const opts = nodeBrushOptions;

    return (
        <>
            <div className="field-row field-row--two">
                <label className="field-label">
                    Radius
                    <DebouncedInput
                        type="number"
                        min={0}
                        step={1}
                        value={opts.radius}
                        onChange={(v) =>
                            setNodeBrushOptions({
                                radius: Math.max(0, num(v, opts.radius)),
                            })
                        }
                    />
                </label>
                <label className="field-label">
                    Nodes
                    <DebouncedInput
                        type="number"
                        min={1}
                        step={1}
                        value={opts.count}
                        onChange={(v) =>
                            setNodeBrushOptions({
                                count: Math.max(
                                    1,
                                    Math.round(num(v, opts.count)),
                                ),
                            })
                        }
                    />
                </label>
            </div>

            <label className="field-label" style={{ marginTop: 8 }}>
                Layout
                <select
                    className="anchor-select"
                    value={opts.layout}
                    onChange={(e) =>
                        setNodeBrushOptions({
                            layout: e.target.value as NodeBrushLayout,
                        })
                    }
                >
                    {LAYOUT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </label>

            <div className="diagram-node-panel-section">
                <CheckboxField
                    checked={opts.connect}
                    onChange={(checked) =>
                        setNodeBrushOptions({ connect: checked })
                    }
                    label="Connect the new nodes"
                />
                {opts.connect && (
                    <label className="field-label" style={{ marginTop: 6 }}>
                        Connection pattern
                        <select
                            className="anchor-select"
                            value={opts.connectMode}
                            onChange={(e) =>
                                setNodeBrushOptions({
                                    connectMode: e.target
                                        .value as NodeBrushConnectMode,
                                })
                            }
                        >
                            <option value="sequence">
                                In sequence (A→B→C→A)
                            </option>
                            <option value="complete">
                                Every node to every other
                            </option>
                        </select>
                    </label>
                )}
                {opts.connect && (
                    <p
                        className="path-tool-meta"
                        style={{ margin: "6px 0 0", opacity: 0.7 }}
                    >
                        Uses the active connection preset.
                    </p>
                )}
            </div>

            <div className="diagram-node-panel-section">
                <CheckboxField
                    checked={opts.group}
                    onChange={(checked) =>
                        setNodeBrushOptions({ group: checked })
                    }
                    label="Group the new nodes"
                />
            </div>
        </>
    );
}

/**
 * Options for the multi-node-brush composite subtool: how many node-brush
 * strokes one click stamps and how their centers spread. The per-cluster
 * settings (size/count/connect/group of each stroke) are the shared node-brush
 * options, shown below under their own heading — this brush literally runs the
 * node brush at each stamped center.
 */
function MultiNodeBrushOptionsPanel() {
    const { multiNodeBrushOptions, setMultiNodeBrushOptions } =
        useSvgDiagram2D();
    const opts = multiNodeBrushOptions;

    return (
        <>
            <div className="field-row field-row--two">
                <label className="field-label">
                    Spread radius
                    <DebouncedInput
                        type="number"
                        min={0}
                        step={1}
                        value={opts.spreadRadius}
                        onChange={(v) =>
                            setMultiNodeBrushOptions({
                                spreadRadius: Math.max(
                                    0,
                                    num(v, opts.spreadRadius),
                                ),
                            })
                        }
                    />
                </label>
                <label className="field-label">
                    Brushes
                    <DebouncedInput
                        type="number"
                        min={1}
                        step={1}
                        value={opts.count}
                        onChange={(v) =>
                            setMultiNodeBrushOptions({
                                count: Math.max(
                                    1,
                                    Math.round(num(v, opts.count)),
                                ),
                            })
                        }
                    />
                </label>
            </div>

            <label className="field-label" style={{ marginTop: 8 }}>
                Brush spread
                <select
                    className="anchor-select"
                    value={opts.layout}
                    onChange={(e) =>
                        setMultiNodeBrushOptions({
                            layout: e.target.value as NodeBrushLayout,
                        })
                    }
                >
                    {LAYOUT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </label>

            <div className="diagram-node-panel-section">
                <CheckboxField
                    checked={opts.groupAll}
                    onChange={(checked) =>
                        setMultiNodeBrushOptions({ groupAll: checked })
                    }
                    label="Group all stamped nodes together"
                />
            </div>

            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Each brush</p>
                <p
                    className="path-tool-meta"
                    style={{ margin: "0 0 8px", opacity: 0.7 }}
                >
                    Every stamped cluster uses the node brush below.
                </p>
                <NodeBrushOptionsPanel />
            </div>
        </>
    );
}

/**
 * Options for the mega-node-brush composite subtool: how many multi-node-brush
 * stamps one click stamps and how their centers spread. The per-stamp settings
 * are the shared multi-node-brush options, shown below under their own heading —
 * this brush literally runs the multi-node brush at each stamped center.
 */
function MegaNodeBrushOptionsPanel() {
    const { megaNodeBrushOptions, setMegaNodeBrushOptions } = useSvgDiagram2D();
    const opts = megaNodeBrushOptions;

    const concentric = opts.mode === "concentric";

    return (
        <>
            <label className="field-label">
                Stamp placement
                <select
                    className="anchor-select"
                    value={opts.mode}
                    onChange={(e) =>
                        setMegaNodeBrushOptions({
                            mode: e.target.value as MegaNodeBrushMode,
                        })
                    }
                >
                    <option value="scatter">Scatter (spread out)</option>
                    <option value="concentric">
                        Concentric (same spot, growing radius)
                    </option>
                </select>
            </label>

            <div className="field-row field-row--two" style={{ marginTop: 8 }}>
                <label className="field-label">
                    Stamps
                    <DebouncedInput
                        type="number"
                        min={1}
                        step={1}
                        value={opts.count}
                        onChange={(v) =>
                            setMegaNodeBrushOptions({
                                count: Math.max(
                                    1,
                                    Math.round(num(v, opts.count)),
                                ),
                            })
                        }
                    />
                </label>
                {concentric ? (
                    <label className="field-label">
                        Radius step
                        <DebouncedInput
                            type="number"
                            min={0}
                            step={1}
                            value={opts.radiusStep}
                            onChange={(v) =>
                                setMegaNodeBrushOptions({
                                    radiusStep: Math.max(
                                        0,
                                        num(v, opts.radiusStep),
                                    ),
                                })
                            }
                        />
                    </label>
                ) : (
                    <label className="field-label">
                        Spread radius
                        <DebouncedInput
                            type="number"
                            min={0}
                            step={1}
                            value={opts.spreadRadius}
                            onChange={(v) =>
                                setMegaNodeBrushOptions({
                                    spreadRadius: Math.max(
                                        0,
                                        num(v, opts.spreadRadius),
                                    ),
                                })
                            }
                        />
                    </label>
                )}
            </div>

            {concentric ? (
                <>
                    <label className="field-label" style={{ marginTop: 8 }}>
                        Brush step
                        <DebouncedInput
                            type="number"
                            min={0}
                            step={1}
                            value={opts.countStep}
                            onChange={(v) =>
                                setMegaNodeBrushOptions({
                                    countStep: Math.max(
                                        0,
                                        Math.round(num(v, opts.countStep)),
                                    ),
                                })
                            }
                        />
                    </label>
                    <p
                        className="path-tool-meta"
                        style={{ margin: "6px 0 0", opacity: 0.7 }}
                    >
                        All stamps land on the click point. Each outer ring grows
                        the multi-node spread radius by the radius step and adds
                        the brush step more clusters, so inner rings are sparse
                        and outer rings fuller.
                    </p>
                </>
            ) : (
                <label className="field-label" style={{ marginTop: 8 }}>
                    Stamp spread
                    <select
                        className="anchor-select"
                        value={opts.layout}
                        onChange={(e) =>
                            setMegaNodeBrushOptions({
                                layout: e.target.value as NodeBrushLayout,
                            })
                        }
                    >
                        {LAYOUT_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </label>
            )}

            <div className="diagram-node-panel-section">
                <CheckboxField
                    checked={opts.groupAll}
                    onChange={(checked) =>
                        setMegaNodeBrushOptions({ groupAll: checked })
                    }
                    label="Group all stamped nodes together"
                />
            </div>

            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Each stamp</p>
                <p
                    className="path-tool-meta"
                    style={{ margin: "0 0 8px", opacity: 0.7 }}
                >
                    Every stamp uses the multi-node brush below.
                </p>
                <MultiNodeBrushOptionsPanel />
            </div>
        </>
    );
}

/**
 * Floating panel showing the active composite subtool's options. Rendered as an
 * overlay on the left of the viewer (next to the subtool rail). Returns null for
 * subtools that have no options.
 *
 * This is the home for composite-subtool parameters: as more composite subtools
 * are added (previous-step-aware brushes, etc.), each gets a branch here,
 * mirroring how the per-element inspector panels switch on the selection.
 */
export function SvgDiagram2DSubtoolPanel() {
    const { tool } = useSvgDiagram2D();

    if (tool === "connect-selection") {
        return (
            <div className="diagram-subtool-panel">
                <div
                    className="field-row"
                    style={{ alignItems: "center", gap: 6 }}
                >
                    <Waypoints size={15} aria-hidden />
                    <strong style={{ flex: 1, minWidth: 0 }}>
                        Connect selection
                    </strong>
                </div>
                <p
                    className="path-tool-meta"
                    style={{ margin: "4px 0 8px", opacity: 0.7 }}
                >
                    Select nodes on the canvas, then connect them by pattern and
                    proximity.
                </p>
                <ConnectSelectionOptionsPanel />
            </div>
        );
    }

    if (tool === "interact") {
        return (
            <div className="diagram-subtool-panel">
                <div
                    className="field-row"
                    style={{ alignItems: "center", gap: 6 }}
                >
                    <Magnet size={15} aria-hidden />
                    <strong style={{ flex: 1, minWidth: 0 }}>
                        Attract / repel / connect
                    </strong>
                </div>
                <p
                    className="path-tool-meta"
                    style={{ margin: "4px 0 8px", opacity: 0.7 }}
                >
                    Click a node to pull, push, or connect its neighbors.
                </p>
                <InteractOptionsPanel />
            </div>
        );
    }

    if (tool === "node-brush") {
        return (
            <div className="diagram-subtool-panel">
                <div
                    className="field-row"
                    style={{ alignItems: "center", gap: 6 }}
                >
                    <Brush size={15} aria-hidden />
                    <strong style={{ flex: 1, minWidth: 0 }}>Node brush</strong>
                </div>
                <p
                    className="path-tool-meta"
                    style={{ margin: "4px 0 8px", opacity: 0.7 }}
                >
                    Click the canvas to drop a cluster of nodes.
                </p>
                <NodeBrushOptionsPanel />
            </div>
        );
    }

    if (tool === "multi-node-brush") {
        return (
            <div className="diagram-subtool-panel">
                <div
                    className="field-row"
                    style={{ alignItems: "center", gap: 6 }}
                >
                    <Paintbrush size={15} aria-hidden />
                    <strong style={{ flex: 1, minWidth: 0 }}>
                        Multi-node brush
                    </strong>
                </div>
                <p
                    className="path-tool-meta"
                    style={{ margin: "4px 0 8px", opacity: 0.7 }}
                >
                    Click the canvas to stamp several node-brush clusters at
                    once.
                </p>
                <MultiNodeBrushOptionsPanel />
            </div>
        );
    }

    if (tool === "mega-node-brush") {
        return (
            <div className="diagram-subtool-panel">
                <div
                    className="field-row"
                    style={{ alignItems: "center", gap: 6 }}
                >
                    <Paintbrush2 size={15} aria-hidden />
                    <strong style={{ flex: 1, minWidth: 0 }}>
                        Mega-node brush
                    </strong>
                </div>
                <p
                    className="path-tool-meta"
                    style={{ margin: "4px 0 8px", opacity: 0.7 }}
                >
                    Click the canvas to stamp several multi-node-brush stamps at
                    once.
                </p>
                <MegaNodeBrushOptionsPanel />
            </div>
        );
    }

    return null;
}
